import { SiteScopePicker } from '@/pages/site/SiteScopePicker';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { ReportFilterParams } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { AdvancedFilters, type AdvancedLocationRow } from './AdvancedFilters';
import styles from './ScopedReportsPage.module.css';

/**
 * Shared Reports shell — owns the chrome (header + banner mount +
 * tab bar + filter bar + URL state) so the operator, SP, and
 * building-admin Reports pages can share one implementation of the
 * pieces that used to be duplicated.
 *
 * Tab BODIES stay co-located with their consumer page (each tab
 * owns its fetchers, KPI calcs, column definitions, charts, CSV
 * export wiring). Tabs reach the shell via the `tabs: TabSpec[]`
 * prop, where each TabSpec's `render` is a closure that captures
 * whatever persona-specific context (agentId, custom filters,
 * etc.) the consumer needs.
 *
 * Persona awareness lives at the consumer site, not in the shell.
 * The shell is persona-agnostic — it just runs the URL state +
 * filter chip selection and hands the resolved (period, locationId)
 * to whichever tab is active.
 *
 * See docs/tickets/building-admin-reports.md for the design lock
 * that this commit implements.
 */

// ── Public types ───────────────────────────────────────────────────

export interface PeriodOption {
  id: string;
  label: string;
  /** When false, the chip renders disabled and clicking is a no-op.
   *  Preserved for back-compat with operator + SP's inline period
   *  arrays (which still set supported:false for 90d/custom because
   *  their underlying endpoints don't accept arbitrary date ranges
   *  yet). STANDARD_PERIOD_OPTIONS below now has all five chips
   *  supported post reports-custom-range. */
  supported: boolean;
  /** Period kind (reports-custom-range). 'preset' chips resolve to
   *  a Nd-back range via `days`; 'custom' triggers the date-range
   *  picker UI below the chip row. Optional + defaults to 'preset'
   *  so consumers (operator + SP) that inline PeriodOption-shaped
   *  arrays without specifying kind keep compiling unchanged. */
  kind?: 'preset' | 'custom';
  /** Days back from now for preset chips. Required when
   *  kind === 'preset' AND supported; ignored when kind === 'custom'
   *  or when supported === false. */
  days?: number;
}

/**
 * Standard period chip set. Single source of truth for the chip
 * inventory across Reports consumers — Building Admin Reports
 * (commit 6) imports this directly; operator + SP currently inline
 * their own copies (commit 3 deferral) and will migrate in a
 * follow-up cleanup that also extends their server endpoints to
 * accept arbitrary date ranges.
 *
 * As of reports-custom-range: 90d and Custom are now BOTH
 * supported. Building Admin's BookingHistoryTab and
 * LocationUnitsConsumedTab consume the resolved from/to range from
 * the shell (via TabRenderState.from/to) — their server endpoints
 * already accept arbitrary ISO date ranges up to the 365-day cap.
 */
export const STANDARD_PERIOD_OPTIONS: ReadonlyArray<PeriodOption> = [
  { id: '24h',    label: '24h',    supported: true, kind: 'preset', days: 1 },
  { id: '7d',     label: '7d',     supported: true, kind: 'preset', days: 7 },
  { id: '30d',    label: '30d',    supported: true, kind: 'preset', days: 30 },
  { id: '90d',    label: '90d',    supported: true, kind: 'preset', days: 90 },
  { id: 'custom', label: 'Custom', supported: true, kind: 'custom' },
];

export interface TabRenderState {
  activeTabId: string;
  period: string;
  /** Resolved ISO start of the period range (reports-custom-range).
   *  The shell does the period -> range math for every chip kind
   *  ('preset' subtracts `days` from now; 'custom' uses the URL-
   *  state from/to). Tab bodies that previously inlined
   *  periodToFromTo(period) can drop their local helper and consume
   *  these directly. Always present and valid post-mount; the
   *  shell never renders tab bodies with a missing or invalid
   *  range. */
  from: string;
  /** Resolved ISO end of the period range — see `from`. */
  to: string;
  locationId: string | null;
  /** Reporting v2 slice 1 — the full filter set, ready to hand to any
   *  report fetcher. Includes the legacy single locationId as a
   *  one-element location_ids when no multi-select is active, so tab
   *  bodies can pass `filters` alone and cover both control styles. */
  filters: ReportFilterParams;
  /** Tab bodies that own a sub-control which can nudge the period
   *  (Revenue / Energy tabs do this today via their RevenuePeriodPill)
   *  call this to push the new period back into the URL state. */
  onPeriodChange: (next: string) => void;
}

export interface TabSpec {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Render the tab body. Receives the resolved filter state at
   *  call time; the consumer's closure can pull in whatever extra
   *  persona context (agentId, locations[], agent row) the body
   *  needs. */
  render: (state: TabRenderState) => ReactNode;
  /** The permission string this tab's data needs, e.g. 'billing:read'.
   *
   *  WHY A TAB NEEDS THIS AT ALL. Several personas share one Reports
   *  page: a fleet_admin's nav leaf points at /reports and App.tsx
   *  branches away only for building_admin and sales_agent, so a
   *  fleet_admin lands on the OPERATOR page. Its seeded matrix carries
   *  no Billing row, so a Payouts tab without this would render for
   *  them and 403 on open.
   *
   *  Omit for a tab whose data everyone on the page can read. When set,
   *  the tab is dropped from the bar AND from the ?tab= allowlist, so a
   *  hand-typed deep link falls back to the default rather than opening
   *  a body whose every request fails. */
  requiresPermission?: string;
}

export interface ScopedReportsPageProps {
  multiLocationFilter?: boolean;
  /** Page header title. */
  title: string;
  headerActions?: ReactNode;
  /** Page header subtitle. */
  subtitle: string;
  /** Tab inventory; order preserved in the tab bar. */
  tabs: ReadonlyArray<TabSpec>;
  /** Tab id rendered when URL has no ?tab=. Must match an entry in
   *  tabs[]; if the URL carries an unknown tab id the shell falls
   *  back to this. */
  defaultTabId: string;
  /** Period chips. Same `{24h,7d,30d enabled; 90d,custom disabled}`
   *  shape for every consumer today; the shell accepts the array as
   *  a prop so a future consumer can ship a different chip set
   *  without touching this file. */
  periodOptions: ReadonlyArray<PeriodOption>;
  /** Period id used when URL has no ?period= (or it's invalid). */
  defaultPeriodId: string;
  /** Locations dropdown population. The consumer fetches its own
   *  list (operator: tenant-wide; SP: server-scoped to assigned
   *  chargers; building admin: server-scoped to assigned locations).
   *  Pass an empty array when there are none / still loading. */
  locations: ReadonlyArray<{ _id: string; name: string }>;
  /** Location dropdown render policy.
   *    'always'     — always render (operator + SP)
   *    'multi-only' — only when locations.length >= 2 (building
   *                   admin — single-location admins don't need a
   *                   pointless one-option dropdown)
   *    'never'      — never render */
  locationFilter: 'always' | 'multi-only' | 'never';
  /** Render order for tab bar vs filter bar. Operator currently uses
   *  'tabs-first' (tab bar above filter bar); SP uses 'filter-first'
   *  (filter bar above tab bar). Both preserved verbatim by this
   *  prop. Building admin can pick either; design pick at the
   *  consumer site. */
  chromeOrder: 'tabs-first' | 'filter-first';
  /** Optional banner stack rendered above the page header. SP fills
   *  this with SalesPartnerConnectBanner; other consumers omit. */
  bannerStack?: ReactNode;
  /** Optional extra slot inside the filter bar, after the location
   *  dropdown. Render function (not static ReactNode) so consumers
   *  can react to the current period / locationId — the operator
   *  uses this for a CSV button whose download URL embeds the
   *  filter state. Mirrors TabSpec.render's closure pattern. SP /
   *  building-admin pass nothing (their tab bodies host per-tab
   *  exports). */
  filterBarExtra?: (state: TabRenderState) => ReactNode;
  /** Reporting v2 slice 1 — when provided, the single-select location
   *  dropdown is replaced by the Advanced filters control (searchable
   *  multi-select + saved groups + geography + type + residential
   *  enrollment), whose options derive from this richer list. The
   *  operator passes its full scoped location docs; SP and building
   *  admin omit it and keep the single select unchanged. */
  advancedLocations?: ReadonlyArray<AdvancedLocationRow>;
}

// ── URL state hook ────────────────────────────────────────────────

interface ReportsUrlState {
  tab: string;
  period: string;
  locationId: string | null;
  /** Custom range start (ISO date YYYY-MM-DD). Reports-custom-range:
   *  only populated when period === 'custom'; null for preset chips.
   *  When period is 'custom' but customFrom/customTo are null/
   *  invalid (deep link landed on ?period=custom with no dates), the
   *  range resolver below falls back to a default 7-day window so
   *  the tab body still renders something while the user picks
   *  dates. */
  customFrom: string | null;
  customTo: string | null;
  /** Reporting v2 slice 1 — the Advanced-filters state, URL-persisted
   *  so a filtered report is shareable. Compiled server-side under
   *  the caller's scope; the URL is a request, never an access grant. */
  locationIds: string[];
  groupId: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  locationTypes: string[];
  residentialEnrollment: 'enrolled' | 'not_enrolled' | null;
}

const OBJECT_ID_REGEX = /^[a-f0-9]{24}$/;
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function readUrlState(opts: {
  defaultTabId: string;
  defaultPeriodId: string;
  isValidTabId: (s: string) => boolean;
  isValidPeriodId: (s: string) => boolean;
}): ReportsUrlState {
  const sp = new URLSearchParams(window.location.search);
  const rawTab = sp.get('tab') ?? opts.defaultTabId;
  const rawPeriod = sp.get('period') ?? opts.defaultPeriodId;
  const locationId = sp.get('location_id');
  const fromRaw = sp.get('from');
  const toRaw = sp.get('to');
  const idList = (v: string | null): string[] =>
    (v ?? '').split(',').map((s) => s.trim()).filter((s) => OBJECT_ID_REGEX.test(s));
  const groupIdRaw = sp.get('group_id');
  const enrollmentRaw = sp.get('residential_enrollment');
  return {
    tab: opts.isValidTabId(rawTab) ? rawTab : opts.defaultTabId,
    period: opts.isValidPeriodId(rawPeriod) ? rawPeriod : opts.defaultPeriodId,
    locationId: locationId && OBJECT_ID_REGEX.test(locationId) ? locationId : null,
    customFrom: fromRaw && DATE_REGEX.test(fromRaw) ? fromRaw : null,
    customTo: toRaw && DATE_REGEX.test(toRaw) ? toRaw : null,
    locationIds: idList(sp.get('location_ids')),
    groupId: groupIdRaw && OBJECT_ID_REGEX.test(groupIdRaw) ? groupIdRaw : null,
    country: sp.get('country'),
    state: sp.get('state'),
    city: sp.get('city'),
    locationTypes: (sp.get('location_types') ?? '').split(',').map((s) => s.trim()).filter(Boolean),
    residentialEnrollment:
      enrollmentRaw === 'enrolled' || enrollmentRaw === 'not_enrolled' ? enrollmentRaw : null,
  };
}

/**
 * Extracted from the inline duplication in operator + SP ReportsPages
 * (was identical structurally; only the default-tab string differed).
 * Now parameterised on defaults + valid-id predicates so each
 * consumer's persona-specific tab/period inventory drives validation.
 *
 * Uses replaceState (not pushState) — filter-pick noise shouldn't
 * flood the back stack. Tab switches OK to collapse too; the user
 * reads the current URL as state, not history. popstate listener
 * picks up external nav (back/forward, sidebar deep-links).
 */
export function useReportsUrlState(opts: {
  defaultTabId: string;
  defaultPeriodId: string;
  isValidTabId: (s: string) => boolean;
  isValidPeriodId: (s: string) => boolean;
}): [ReportsUrlState, (next: Partial<ReportsUrlState>) => void] {
  const [state, setState] = useState<ReportsUrlState>(() => readUrlState(opts));

  useEffect(() => {
    // opts is stable per consumer mount (literal recreated each
    // render but semantics don't change at runtime). Deliberately
    // skipping opts in the deps array so the listener doesn't
    // resubscribe on every render. The admin app's ESLint config
    // doesn't enable react-hooks/exhaustive-deps so no disable
    // directive is needed.
    const onPop = (): void => setState(readUrlState(opts));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const update = useCallback((next: Partial<ReportsUrlState>) => {
    setState((prev) => {
      const merged = { ...prev, ...next };
      const sp = new URLSearchParams(window.location.search);
      sp.set('tab', merged.tab);
      sp.set('period', merged.period);
      if (merged.locationId) sp.set('location_id', merged.locationId);
      else sp.delete('location_id');
      // reports-custom-range: from/to only persist when period is
      // 'custom'. Switching back to a preset clears them so the URL
      // doesn't carry stale range params that would confuse a
      // subsequent ?period=custom landing without explicit dates.
      if (merged.period === 'custom' && merged.customFrom) {
        sp.set('from', merged.customFrom);
      } else {
        sp.delete('from');
      }
      if (merged.period === 'custom' && merged.customTo) {
        sp.set('to', merged.customTo);
      } else {
        sp.delete('to');
      }
      // Advanced filters (Reporting v2 slice 1): persist non-empty,
      // delete empty, so the URL stays clean and shareable.
      if (merged.locationIds.length > 0) sp.set('location_ids', merged.locationIds.join(','));
      else sp.delete('location_ids');
      if (merged.groupId) sp.set('group_id', merged.groupId);
      else sp.delete('group_id');
      for (const [key, val] of [
        ['country', merged.country], ['state', merged.state], ['city', merged.city],
      ] as const) {
        if (val) sp.set(key, val);
        else sp.delete(key);
      }
      if (merged.locationTypes.length > 0) sp.set('location_types', merged.locationTypes.join(','));
      else sp.delete('location_types');
      if (merged.residentialEnrollment) sp.set('residential_enrollment', merged.residentialEnrollment);
      else sp.delete('residential_enrollment');
      const newUrl = `${window.location.pathname}?${sp.toString()}`;
      window.history.replaceState(window.history.state, '', newUrl);
      return merged;
    });
  }, []);

  return [state, update];
}

// ── Range resolution ──────────────────────────────────────────────

/** Resolve the active period chip + custom date inputs to the full
 *  ISO date range tab bodies + filterBarExtra closures consume.
 *
 *  Preset chips: subtract `days` (from the chip's option entry) from
 *  now. Falls back to 7 days when the chip exists but has no `days`
 *  (legacy PeriodOption-shaped consumer that didn't migrate to the
 *  reports-custom-range additive fields — operator + SP still emit
 *  arrays in this shape).
 *
 *  Custom chip: use the URL-state customFrom/customTo. When either
 *  is null (deep link landed on ?period=custom without explicit
 *  dates) OR from >= to (user typed an invalid range), fall back to
 *  the same 7-day default so tab bodies still render coherent data
 *  while the user picks. Server-side 365-day cap is the
 *  authoritative limit; client-side is informational only. */
function resolveRange(
  period: string,
  customFrom: string | null,
  customTo: string | null,
  periodOptions: ReadonlyArray<PeriodOption>,
): { from: string; to: string } {
  const opt = periodOptions.find((p) => p.id === period);

  // Custom kind branch — try URL dates first; fall back to 7d default
  // when missing or inverted.
  if (opt?.kind === 'custom' || period === 'custom') {
    if (customFrom && customTo && customFrom < customTo) {
      // Interpret YYYY-MM-DD as the local-midnight-to-local-midnight
      // boundaries the user picked. Server-side endpoints accept any
      // ISO date string and parse uniformly.
      const from = new Date(`${customFrom}T00:00:00.000Z`).toISOString();
      const to = new Date(`${customTo}T23:59:59.999Z`).toISOString();
      return { from, to };
    }
    return defaultSevenDays();
  }

  // Preset kind branch (default).
  const days = opt?.days ?? 7;
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function defaultSevenDays(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Today as YYYY-MM-DD for the date picker's max attribute (prevents
 *  picking future dates that would always return empty data). */
function todayDateString(): string {
  return new Date().toISOString().slice(0, 10);
}

// ── Sub-components ─────────────────────────────────────────────────

function TabBar({
  tabs, active, onChange,
}: {
  tabs: ReadonlyArray<TabSpec>;
  active: string;
  onChange: (next: string) => void;
}): JSX.Element | null {
  const nav = useRef<HTMLElement>(null);
  useEffect(() => {
    const bar = nav.current, button = bar?.querySelector<HTMLElement>('[aria-current="page"]');
    if (bar && button && bar.scrollWidth > bar.clientWidth) {
      bar.scrollLeft += button.getBoundingClientRect().left - bar.getBoundingClientRect().left - (bar.clientWidth - button.clientWidth) / 2;
    }
  }, [active]);
  // A one-tab surface has no choice to offer, so the bar is chrome
  // that only says "you are where you already are". Suppressing it
  // lets a promoted single-tab page (SP Commissions) read as a page
  // rather than a report someone forgot to finish. The shell still
  // owns ?tab= state for it, so deep links keep working.
  if (tabs.length <= 1) return null;

  return (
    <nav ref={nav} className={styles.tabBar} aria-label="Report tabs">
      {tabs.map((t) => {
        const Icon = t.icon;
        const isActive = active === t.id;
        return (
          <button
            key={t.id}
            type="button"
            className={`${styles.tabBtn} ${isActive ? styles.tabBtnActive : ''}`}
            onClick={() => onChange(t.id)}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon size={14} />
            <span>{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function FilterBar({
  periodOptions, period, onPeriodChange,
  locations, locationId, onLocationChange,
  showLocationDropdown, filterBarExtra,
  customFrom, customTo, onCustomRangeChange,
}: {
  periodOptions: ReadonlyArray<PeriodOption>;
  period: string;
  onPeriodChange: (next: string) => void;
  locations: ReadonlyArray<{ _id: string; name: string }>;
  locationId: string | null;
  onLocationChange: (next: string | null) => void;
  showLocationDropdown: boolean;
  filterBarExtra?: ReactNode;
  customFrom: string | null;
  customTo: string | null;
  onCustomRangeChange: (next: { customFrom?: string | null; customTo?: string | null }) => void;
}): JSX.Element {
  // Note: this internal sub-component receives filterBarExtra as
  // ReactNode (already-resolved); the public-prop render function is
  // resolved at the parent ScopedReportsPage layer below.
  const isCustom = period === 'custom';
  const today = todayDateString();
  // Inline validation banner — server is the authoritative gate
  // (rejects > 365 days with 400) but a fast client-side check
  // catches the obvious "from after to" case before a query fires.
  const customInvalid = isCustom
    && customFrom !== null
    && customTo !== null
    && customFrom >= customTo;

  return (
    <div className={styles.filterBar}>
      <div className={styles.periodPills} role="radiogroup" aria-label="Date range">
        {periodOptions.map((p) => {
          const isActive = period === p.id;
          const disabled = !p.supported;
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={isActive}
              disabled={disabled}
              title={disabled ? 'Extended date ranges land in a future phase' : undefined}
              className={`${styles.pill} ${isActive ? styles.pillActive : ''} ${disabled ? styles.pillDisabled : ''}`}
              onClick={() => p.supported && onPeriodChange(p.id)}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Custom date-range picker — renders inline when the Custom
          chip is active. Native <input type="date"> matches the
          existing CouponsPage / RevenuePage convention; no
          calendar-widget library imported (admin app has none and
          this isn't the commit to introduce one). */}
      {isCustom && (
        <div className={styles.customRange}>
          <label className={styles.customRangeLabel}>
            <span>From</span>
            <input
              type="date"
              className={styles.customRangeInput}
              value={customFrom ?? ''}
              max={today}
              onChange={(e) => onCustomRangeChange({ customFrom: e.target.value || null })}
              aria-label="Custom range start date"
            />
          </label>
          <label className={styles.customRangeLabel}>
            <span>To</span>
            <input
              type="date"
              className={styles.customRangeInput}
              value={customTo ?? ''}
              max={today}
              onChange={(e) => onCustomRangeChange({ customTo: e.target.value || null })}
              aria-label="Custom range end date"
            />
          </label>
          {customInvalid && (
            <span className={styles.customRangeError} role="alert">
              From must be before To
            </span>
          )}
        </div>
      )}

      {showLocationDropdown && (
        <select
          className={styles.locationSelect}
          value={locationId ?? ''}
          onChange={(e) => onLocationChange(e.target.value === '' ? null : e.target.value)}
          aria-label="Location filter"
        >
          <option value="">All locations</option>
          {locations.map((l) => (
            <option key={l._id} value={l._id}>{l.name}</option>
          ))}
        </select>
      )}

      {filterBarExtra && (
        <div className={styles.filterBarExtra}>{filterBarExtra}</div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────

export function ScopedReportsPage(props: ScopedReportsPageProps): JSX.Element {
  const {
    title, subtitle, headerActions, tabs: allTabs, defaultTabId, periodOptions, defaultPeriodId,
    locations, locationFilter, chromeOrder, bannerStack, filterBarExtra,
    advancedLocations,
  } = props;

  // NO TAB THAT 403s. A tab declaring requiresPermission is dropped
  // unless this viewer holds that string, and validTabIds is built from
  // the SURVIVORS — so a hand-typed ?tab=payouts from a persona without
  // billing:read falls back to the default instead of opening a body
  // whose every request fails. super_admin bypasses, mirroring
  // requireMatrixPermission, because its derived string set is
  // incomplete by design.
  const viewer = useAuthStore((s) => s.user);
  const tabs = useMemo(() => allTabs.filter((t) => {
    if (!t.requiresPermission) return true;
    if (viewer?.role === 'super_admin') return true;
    return (viewer?.permissions ?? []).includes(t.requiresPermission);
  }), [allTabs, viewer]);

  const validTabIds = new Set(tabs.map((t) => t.id));
  const validPeriodIds = new Set(periodOptions.map((p) => p.id));

  const [urlState, setUrlState] = useReportsUrlState({
    defaultTabId,
    defaultPeriodId,
    isValidTabId: (s) => validTabIds.has(s),
    isValidPeriodId: (s) => validPeriodIds.has(s),
  });
  const {
    tab, period, locationId, customFrom, customTo,
    locationIds, groupId, country, state, city, locationTypes, residentialEnrollment,
  } = urlState;
  // Resolved range — single source of truth threaded through to
  // TabRenderState. Tab bodies that previously inlined their own
  // periodToFromTo can drop the helper and consume `from`/`to`
  // directly.
  const { from, to } = useMemo(() => resolveRange(period, customFrom, customTo, periodOptions),
    [period, customFrom, customTo, periodOptions]);

  const showLocationDropdown =
    locationFilter === 'always' ||
    (locationFilter === 'multi-only' && locations.length >= 2);

  // Active tab — fall back to defaultTab if URL state somehow points
  // at an unknown tab id (the URL validator should prevent this but
  // belt-and-suspenders for forward-compat when consumers change
  // their tabs[] inventory).
  const activeTab = tabs.find((t) => t.id === tab) ?? tabs.find((t) => t.id === defaultTabId);

  // The full filter set every tab fetcher and CSV consumes. The
  // legacy single-select locationId folds in as a one-element pick
  // so both control styles compile to the same wire params.
  const filters: ReportFilterParams = {
    location_ids: locationIds.length > 0
      ? locationIds
      : (locationId ? [locationId] : undefined),
    group_id: groupId,
    country,
    state,
    city,
    location_types: locationTypes.length > 0 ? locationTypes : undefined,
    residential_enrollment: residentialEnrollment,
  };

  const tabRenderState: TabRenderState = {
    activeTabId: tab,
    period,
    from,
    to,
    locationId,
    filters,
    onPeriodChange: (next) => setUrlState({ period: next }),
  };

  // Removable applied-filter chips (Advanced control only).
  const chips: Array<{ key: string; label: string; clear: Partial<ReportsUrlState> }> = [];
  if (advancedLocations) {
    if (locationIds.length > 0) {
      const names = locationIds
        .map((id) => advancedLocations.find((l) => l._id === id)?.name ?? '1 location');
      chips.push({
        key: 'locations',
        label: locationIds.length <= 2 ? names.join(', ') : `${locationIds.length} locations`,
        clear: { locationIds: [] },
      });
    }
    if (groupId) chips.push({ key: 'group', label: 'Saved group', clear: { groupId: null } });
    if (country) chips.push({ key: 'country', label: country, clear: { country: null } });
    if (state) chips.push({ key: 'state', label: state, clear: { state: null } });
    if (city) chips.push({ key: 'city', label: city, clear: { city: null } });
    if (locationTypes.length > 0) {
      chips.push({ key: 'types', label: locationTypes.join(', '), clear: { locationTypes: [] } });
    }
    if (residentialEnrollment) {
      chips.push({
        key: 'enrollment',
        label: residentialEnrollment === 'enrolled' ? 'Residential: enrolled' : 'Residential: not enrolled',
        clear: { residentialEnrollment: null },
      });
    }
  }

  const tabBar = (
    <TabBar
      tabs={tabs}
      active={tab}
      onChange={(next) => setUrlState({ tab: next })}
    />
  );

  const filterBar = (
    <FilterBar
      periodOptions={periodOptions}
      period={period}
      onPeriodChange={(next) => {
        // Reports-custom-range: switching AWAY from custom clears
        // the custom range from the URL so a subsequent custom-chip
        // pick starts fresh (avoids stale params bleeding through).
        // Switching INTO custom keeps any existing range (deep-link
        // friendly).
        if (next === 'custom') {
          setUrlState({ period: next });
        } else {
          setUrlState({ period: next, customFrom: null, customTo: null });
        }
      }}
      locations={locations}
      locationId={locationId}
      onLocationChange={(next) => setUrlState({ locationId: next })}
      showLocationDropdown={showLocationDropdown && !advancedLocations && !props.multiLocationFilter}
      filterBarExtra={(
        <>
          {props.multiLocationFilter && <SiteScopePicker sites={locations} ids={locationIds.length ? locationIds : locationId ? [locationId] : []} onChange={(ids) => setUrlState({ locationIds: ids, locationId: null })} />}
          {advancedLocations && (
            <AdvancedFilters
              locations={advancedLocations}
              value={{
                locationIds, groupId, country, state, city,
                locationTypes, residentialEnrollment,
              }}
              onChange={(next) => setUrlState(next)}
            />
          )}
          {filterBarExtra?.(tabRenderState)}
        </>
      )}
      customFrom={customFrom}
      customTo={customTo}
      onCustomRangeChange={(next) => setUrlState(next)}
    />
  );

  const chipsRow = chips.length > 0 ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0' }}>
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => setUrlState(c.clear)}
          aria-label={`Remove filter: ${c.label}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 999, fontSize: 12,
            background: 'var(--color-brand-bg)', color: 'var(--color-tx)',
            border: '1px solid var(--color-brd)', cursor: 'pointer',
          }}
        >
          {c.label} <span aria-hidden>×</span>
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div className={styles.page}>
      {bannerStack && (
        <div className={styles.bannerStack}>{bannerStack}</div>
      )}

      <header className={styles.header}>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>{subtitle}</p>
        {headerActions}
      </header>

      {chromeOrder === 'tabs-first' ? (
        <>
          {tabBar}
          {filterBar}
        </>
      ) : (
        <>
          {filterBar}
          {tabBar}
        </>
      )}

      {chipsRow}

      {activeTab?.render(tabRenderState)}
    </div>
  );
}
